import clsx from 'clsx'
import PropTypes from 'prop-types'

import { anim, pv } from '../motion'
import { COLORS } from '../primitives'

/**
 * Figuren für das Spielbrett der Version 10. Menschen sind Aivy-Grundformen
 * mit Augen und Füßen, flach und ohne Schatten. Alle Bewegungen hängen an
 * `--l` der Section, React rendert dafür nicht neu.
 */

const range = PropTypes.arrayOf(PropTypes.number)
const n = (v) => Number(v.toFixed(4))

/**
 * Weg über mehrere Schlüsselbilder. `from` ist der Startzustand, jedes
 * Schlüsselbild ändert x, y, s (Skalierung) oder o (Deckkraft) im Bereich
 * `at`. Die Bereiche dürfen sich nicht überschneiden.
 */
export function move(from, keys = []) {
  const { x, y, s = 1, o = 1 } = from
  const vars = {}
  let tx = `${x}px`
  let ty = `${y}px`
  let ts = `${s}`
  let to = `${o}`
  const cur = { x, y, s, o }
  keys.forEach((k, i) => {
    const name = `m${i}`
    Object.assign(vars, pv(name, k.at))
    const v = `var(--${name})`
    if (k.x != null && k.x !== cur.x) {
      tx += ` + ${n(k.x - cur.x)}px * ${v}`
      cur.x = k.x
    }
    if (k.y != null && k.y !== cur.y) {
      ty += ` + ${n(k.y - cur.y)}px * ${v}`
      cur.y = k.y
    }
    if (k.s != null && k.s !== cur.s) {
      ts += ` + ${n(k.s - cur.s)} * ${v}`
      cur.s = k.s
    }
    if (k.o != null && k.o !== cur.o) {
      to += ` + ${n(k.o - cur.o)} * ${v}`
      cur.o = k.o
    }
  })
  return {
    ...vars,
    transform: `translate(calc(${tx}), calc(${ty})) scale(calc(${ts}))`,
    opacity: `clamp(0, ${to}, 1)`
  }
}

/** Ein- und Ausblenden ohne Bewegung, für Teile innerhalb einer Figur. */
export const fade = (at, out) => ({
  ...pv('f', at),
  ...(out ? pv('g', out) : { '--g': 0 }),
  opacity: 'calc(var(--f) * (1 - var(--g)))'
})

/** Etwas an einem Weltpunkt, Füße bzw. Unterkante auf y. */
export function At({ x, y, style, className, children }) {
  return (
    <div
      className={clsx('v10-at', className)}
      style={{ transform: `translate(${x}px, ${y}px)`, ...style }}
    >
      {children}
    </div>
  )
}

At.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  style: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node
}

/** Bewegtes Element, `from` und `keys` wie bei move(). */
export function Mover({ from, keys, className, children }) {
  return (
    <div className={clsx('v10-at', className)} style={move(from, keys)}>
      {children}
    </div>
  )
}

Mover.propTypes = {
  from: PropTypes.object.isRequired,
  keys: PropTypes.array,
  className: PropTypes.string,
  children: PropTypes.node
}

const EYE_Y = { dot: 32, sq: 30, tri: 42 }
const EYE_DX = { dot: 8, sq: 9, tri: 6 }

function Body({ kind, fill }) {
  if (kind === 'dot') return <circle cx='30' cy='34' r='25' fill={fill} />
  if (kind === 'sq')
    return <rect x='6' y='10' width='48' height='48' rx='11' fill={fill} />
  return (
    <path
      d='M30 8 L54 56 H6 Z'
      fill={fill}
      stroke={fill}
      strokeWidth='7'
      strokeLinejoin='round'
    />
  )
}

Body.propTypes = {
  kind: PropTypes.oneOf(['dot', 'sq', 'tri']).isRequired,
  fill: PropTypes.string.isRequired
}

/**
 * Eine Figur. `stress` ist ein Bereich, in dem sie gestresst schaut, `cheer`
 * einer, in dem sie sich freut. `children` hängen über dem Kopf.
 */
export function Fig({
  kind = 'dot',
  color = 'blue',
  size = 56,
  name,
  nameAt,
  stress,
  cheer,
  delay = 0,
  extra,
  className,
  children
}) {
  const fill = COLORS[color] || color
  const ey = EYE_Y[kind]
  const dx = EYE_DX[kind]
  return (
    <div
      className={clsx('v10-fig', className)}
      style={{ width: size, '--d': `${-delay}s` }}
    >
      <div className='v10-fig__bob'>
        <svg
          viewBox='0 0 60 72'
          width={size}
          height={size * 1.2}
          aria-hidden='true'
        >
          <rect x='19' y='60' width='8' height='10' rx='4' fill={COLORS.ink} />
          <rect x='33' y='60' width='8' height='10' rx='4' fill={COLORS.ink} />
          <Body kind={kind} fill={fill} />
          {extra}
          <g className='v10-fig__eyes'>
            <circle cx={30 - dx} cy={ey} r='5.5' fill='#fff' />
            <circle cx={30 + dx} cy={ey} r='5.5' fill='#fff' />
            <circle cx={30 - dx + 1} cy={ey + 1} r='2.6' fill={COLORS.ink} />
            <circle cx={30 + dx + 1} cy={ey + 1} r='2.6' fill={COLORS.ink} />
          </g>
          <path
            d={`M${30 - 5} ${ey + 9} Q30 ${ey + 14} ${30 + 5} ${ey + 9}`}
            fill='none'
            stroke={COLORS.ink}
            strokeWidth='2.4'
            strokeLinecap='round'
            style={
              stress
                ? { ...pv('s', stress), opacity: 'calc(1 - var(--s))' }
                : undefined
            }
          />
          {stress && (
            <g style={fade(stress, cheer)}>
              <path
                d={`M${30 - 4} ${ey + 11} H${30 + 4}`}
                stroke={COLORS.ink}
                strokeWidth='2.4'
                strokeLinecap='round'
              />
              <path
                d='M52 10 C55 15 57 18 57 21 A5 5 0 0 1 47 21 C47 18 49 15 52 10 Z'
                fill='#7FD3E6'
                className='v10-sweat'
              />
            </g>
          )}
          {cheer && (
            <g style={fade(cheer)}>
              <path
                d={`M${30 - 6} ${ey + 8} Q30 ${ey + 17} ${30 + 6} ${ey + 8} Z`}
                fill={COLORS.ink}
              />
            </g>
          )}
        </svg>
        {children}
      </div>
      {name && (
        <span
          className='v10-fig__name'
          style={nameAt ? anim({ at: nameAt, y: 6, s: 0.8 }) : undefined}
        >
          {name}
        </span>
      )}
    </div>
  )
}

Fig.propTypes = {
  kind: PropTypes.oneOf(['dot', 'sq', 'tri']),
  color: PropTypes.string,
  size: PropTypes.number,
  name: PropTypes.string,
  nameAt: range,
  stress: range,
  cheer: range,
  delay: PropTypes.number,
  extra: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node
}

/** Etwas über dem Kopf einer Figur, z.B. Badge oder Sprechblase. */
export function Pop({ at, out, dy = 0, className, children }) {
  return (
    <div
      className={clsx('v10-pop', className)}
      style={{
        marginBottom: dy,
        ...anim({ at, out, y: 10, oy: -6, s: 0.4 })
      }}
    >
      {children}
    </div>
  )
}

Pop.propTypes = {
  at: range.isRequired,
  out: range,
  dy: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node
}

/** Ein Lebenslauf als Blatt Papier. */
export function Paper({ tone, rotate = 0 }) {
  return (
    <div
      className={clsx('v10-paper', tone && `v10-paper--${tone}`)}
      style={{ rotate: `${rotate}deg` }}
    >
      <i className='v10-paper__photo' />
      <i className='v10-paper__line' />
      <i className='v10-paper__line is-short' />
      <i className='v10-paper__line' />
      <i className='v10-paper__line is-short' />
    </div>
  )
}

Paper.propTypes = {
  tone: PropTypes.string,
  rotate: PropTypes.number
}
